<template>
  <div class="relative flex items-center justify-center">
    <div class="absolute top-1/2 h-0-25 w-full bg-gray-300" />
    <div class="z-10 w-auto bg-disabled px-2 whitespace-nowrap">
      <button
        class="relative my-4 cursor-pointer select-none"
        data-cy="add-opposite-condition"
        @click.stop="handleAutoPopulateOppositeCondition"
      >
        <span
          class="rounded-full bg-red-200/50 px-2 py-1 text-xs font-semibold text-red-500 uppercase"
        >
          {{ $t("ORGANIZATION.DO_AUTO_POPULATE_OPPOSITE_CONDITION") }}
        </span>
      </button>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed } from "vue";
import { useStore } from "vuex";

import { ValidOppositeConditions } from "@/enums/orchestration";
import {
  OPPOSITE_CONDITIONS,
  VALID_OPPOSITE_TYPES,
} from "@/helpers/constants/orchestration";
import { useDataOrchestration } from "@/hooks/orchestration";

import type {
  DataOrchestrationOutcome,
  IConditionalSetValueModel,
  IStep,
} from "@/models/orchestration";
import type { PropType } from "vue";

const props = defineProps({
  step: {
    type: Object as PropType<IStep | null>,
  },
  selectedConditionTypesAndValues: {
    type: Object as PropType<IConditionalSetValueModel[]>,
  },
  activeConditionOutcome: {
    type: Object as PropType<DataOrchestrationOutcome>,
  },
});

const { commit } = useStore();
const { getPositionForNewOutcome } = useDataOrchestration();

const conditionData = computed(() =>
  props.activeConditionOutcome === ValidOppositeConditions.FAIL
    ? { title: "Pass", id: ValidOppositeConditions.PASS }
    : { title: "Fail", id: ValidOppositeConditions.FAIL }
);

const handleAutoPopulateOppositeCondition = () => {
  if (!props.step?.id) {
    return;
  }

  const currentOrchestrationStepTypes =
    props.selectedConditionTypesAndValues?.flatMap(({ type }) => type);

  if (!currentOrchestrationStepTypes?.length) {
    return;
  }

  const hasInvalidType = currentOrchestrationStepTypes.some(
    (type) => !VALID_OPPOSITE_TYPES.includes(type)
  );

  if (hasInvalidType) {
    return;
  }

  const currentOutcome = props.step?.outcomes.find(
    ({ outcome }) => outcome === props.activeConditionOutcome
  );

  if (!currentOutcome?.checks) {
    return;
  }

  const checks = currentOutcome.checks.map((check) => ({
    ...check,
    conditions: check.conditions.map((condition) => ({
      ...condition,
      conditions: condition.conditions.map((innerCondition) =>
        innerCondition.type === "boolean"
          ? {
              type: innerCondition.type,
              value: !innerCondition.value,
            }
          : {
              type: OPPOSITE_CONDITIONS[innerCondition.type],
              value: innerCondition.value,
            }
      ),
    })),
  }));

  const position = getPositionForNewOutcome(props.step.id);

  commit("orchestration/appendNextConditional", {
    data: { ...conditionData.value, checks },
    stepId: props.step.id,
    position,
  });
};
</script>
